/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * kCoreHandler — error handling and pure utility functions.
 *
 * Provides:
 *   - ICoreUtils:       interface for injectable error handler
 *   - DefaultCoreUtils: server-safe implementation (logs to console, no UI)
 *   - SetCoreUtils():   replaces the handler at runtime (called by kalmstromShared4
 *                       to register ks, or by FunctionErrorHandler in Azure Functions)
 *   - Utility functions matching the ks.nz / ks.nb / ks.n0 / ... helpers
 *
 * No browser APIs. Safe to import in Node.js, Azure Functions, or the browser.
 */

// ── Error-handler interface ───────────────────────────────────────────────────

export interface ICoreUtils {
    /** When true, methods return early to prevent cascading errors after a failure. */
    readonly kErrorMode: boolean;
    /** Record an error from the given code location. Must not show UI to the user. */
    kGlobalErrorHandler(e: any, location: string): void;
}

// ── Default implementation ────────────────────────────────────────────────────
// Logs to the console only — no dialogs, no network calls, no user interaction.

class DefaultCoreUtils implements ICoreUtils {
    public kErrorMode = false;

    public kGlobalErrorHandler = (e: any, location: string): void => {
        const Message: string = e instanceof Error ? e.message : String(e);
        console.error(`[kCore:${location}] ${Message}`);
    };
}

// ── Module-level singleton ────────────────────────────────────────────────────

let _cu: ICoreUtils = new DefaultCoreUtils();

/**
 * Replace the active error handler.
 * Call once at startup:
 *   - Browser: kalmstromShared4 calls SetCoreUtils(ks)
 *   - Azure Function: pass a FunctionErrorHandler instance
 */
export const SetCoreUtils = (utils: ICoreUtils): void => { _cu = utils; };

/** Read the active handler (used internally by kCoreTime and kCoreObjects). */
export const getCoreUtils = (): ICoreUtils => _cu;

// ── Pure utility functions ────────────────────────────────────────────────────
// Server-safe equivalents of the ks helper methods.

/** Returns the string value of v, or "" when v is null/undefined. */
export const nz = (v: any): string => (v == null ? "" : String(v));

/** Returns the boolean value of v, or false when v is null/undefined. */
export const nb = (v: any): boolean => (v == null ? false : Boolean(v));

/** Returns the numeric value of v, or 0 when v is null/undefined. */
export const n0 = (v: any): number => (v == null ? 0 : Number(v));

/** True when v is neither null nor undefined. */
export const kNotNullOrUndefined = (v: any): boolean => v != null;

/** True when v is a non-empty string. */
export const kNotEmptyString = (v: any): boolean =>
    typeof v === "string" && v.length > 0;

/** Round a number to at most two decimal places. */
export const roundToTwo = (v: number): number => Math.round(v * 100) / 100;

/** Cryptographically-weak random integer in [min, max] (inclusive). */
export const getRandomInt = (min: number, max: number): number =>
    Math.floor(Math.random() * (max - min + 1)) + min;

/** Invoke cb as a function if it is one, passing the remaining args. */
export const doCallBack = (cb: any, ...args: any[]): void => {
    if (typeof cb === "function") (cb as (...a: any[]) => void)(...args);
};

/**
 * Returns the system locale tag (e.g. "en-US").
 * Uses Intl.DateTimeFormat — available in Node.js ≥ 13 and all modern browsers.
 */
export const serverLocale = (): string => {
    try { return Intl.DateTimeFormat().resolvedOptions().locale; }
    catch { return "en-US"; }
};
